// remainder — shared types, palettes, and the pure weave function
// This file is imported by both the Worker and the client for optimistic rendering.

export type WeatherId =
  | 'dense_fog'          // Scattered High Pressure, heavy shoulds
  | 'brittle_clear'      // Too sharp, no room for error
  | 'scattered'          // Electric, jumping between things
  | 'low_static'         // Numb competence, background hum
  | 'electric_overcast'  // Something is coming, attention prickling
  | 'held_breath'        // Waiting, suspended, can't quite land
  | 'paper_thin'         // Everything feels fragile and loud
  | 'slow_current'       // Moving but not choosing

export const WEATHERS: { id: WeatherId; label: string; desc: string }[] = [
  { id: 'dense_fog', label: 'Dense Fog of Shoulds', desc: 'Everything feels thick with what I ought to be doing.' },
  { id: 'brittle_clear', label: 'Brittle Clear', desc: 'No margin. One crack and the whole day shatters.' },
  { id: 'scattered', label: 'Scattered High Pressure', desc: 'Attention in ten places, none of them chosen.' },
  { id: 'low_static', label: 'Low Static', desc: 'Numb competence. I am getting things done and feeling nothing.' },
  { id: 'electric_overcast', label: 'Electric Overcast', desc: 'Something is coming. I keep checking the sky.' },
  { id: 'held_breath', label: 'Held Breath', desc: 'Suspended. I cannot quite land on what needs saying.' },
  { id: 'paper_thin', label: 'Paper Thin', desc: 'Everything feels loud and fragile at the same time.' },
  { id: 'slow_current', label: 'Slow Current', desc: 'I am moving but the motion does not feel like mine.' },
]

export const RESIDUE_TAGS = [
  'hollowness',
  'quiet_dread',
  'relief_tinged_guilt',
  'performative_lightness',
  'numb_competence',
  'low_boil_anger',
  'tender_avoidance',
  'efficient_sadness',
  'protective_numbness',
  'thin_patience',
] as const

export type ResidueTag = typeof RESIDUE_TAGS[number]

export interface Deposit {
  id: string
  created_at: number
  optimization_text: string
  overconfidence: number
  redaction_json?: string | null
  weather_id: WeatherId
  weather_note?: string | null
  residue_tags: ResidueTag[]
  residue_intensity: number
  agency_rating: number
  unresolved_text?: string | null
}

export interface Resonance {
  id: string
  cluster_key: string
  resonance_text: string
  created_at: number
}

export interface ReplaySnapshot {
  id: string
  woven_at: number
  summary_lines: string[]
  stats: {
    depositCount: number
    meanAgency: number
    dominantWeather: string
    knotCount: number
    heldQuestions: number
  }
  weave: {
    threads: Thread[]
    clusters: Cluster[]
    echoes: Echo[]
  }
}

export interface Thread {
  id: string
  weather: WeatherId
  agency: number
  residue: ResidueTag[]
  path: [number, number][]   // normalized 0-1 coordinates for canvas
}

export interface Cluster {
  key: string
  label: string
  x: number
  y: number
  size: number
  meanAgency: number
  dominantResidue: ResidueTag
  count: number
}

export interface Echo {
  cluster_key: string
  text: string
}

// Pure deterministic weave — the loom itself.
// Takes a window of deposits + any new resonances and returns a fresh snapshot.
// No side effects. Safe to call on client for preview or on worker.
export function weave(
  deposits: Deposit[],
  resonances: Resonance[] = [],
  _previousSnapshotId?: string
): Omit<ReplaySnapshot, 'id' | 'woven_at'> {
  const active = deposits.filter(d => !d.unresolved_text || d.unresolved_text.length < 280) // light filter

  if (active.length === 0) {
    return {
      summary_lines: [
        'The loom is quiet. No new remainders have been placed since the last pass.',
        'The commons is holding whatever was woven before.',
      ],
      stats: { depositCount: 0, meanAgency: 50, dominantWeather: '—', knotCount: 0, heldQuestions: 0 },
      weave: { threads: [], clusters: [], echoes: [] },
    }
  }

  const meanAgency = Math.round(active.reduce((s, d) => s + d.agency_rating, 0) / active.length)

  // Simple thematic bucketing (no LLM, pure and legible)
  const buckets: Record<string, Deposit[]> = {}
  for (const d of active) {
    const key = themeKey(d)
    if (!buckets[key]) buckets[key] = []
    buckets[key].push(d)
  }

  const sortedBuckets = Object.entries(buckets).sort((a, b) => b[1].length - a[1].length)

  const clusters: Cluster[] = sortedBuckets.slice(0, 5).map(([key, deps], i) => {
    const x = 0.18 + (i % 3) * 0.28 + (Math.sin(i) * 0.04)
    const y = 0.22 + Math.floor(i / 3) * 0.38 + (i % 2 ? 0.06 : 0)
    const meanA = Math.round(deps.reduce((s, d) => s + d.agency_rating, 0) / deps.length)
    const domRes = mostCommonResidue(deps)
    return {
      key,
      label: humanLabel(key),
      x: Math.max(0.08, Math.min(0.92, x)),
      y: Math.max(0.12, Math.min(0.86, y)),
      size: Math.min(3.6, 1.1 + deps.length * 0.22),
      meanAgency: meanA,
      dominantResidue: domRes,
      count: deps.length,
    }
  })

  // Build organic-ish threads (one per deposit, bent toward its cluster)
  const threads: Thread[] = active.slice(0, 42).map((d, idx) => {
    const cluster = clusters.find(c => c.key === themeKey(d)) || clusters[0]
    const baseX = 0.08 + (idx % 7) * 0.11
    const baseY = 0.15 + Math.floor(idx / 7) * 0.13
    const pullX = (cluster.x - baseX) * (0.35 + (d.agency_rating / 180))
    const pullY = (cluster.y - baseY) * (0.4 + (100 - d.agency_rating) / 220)

    const path: [number, number][] = [
      [baseX, baseY],
      [baseX + pullX * 0.4, baseY + pullY * 0.55],
      [cluster.x + (idx % 3 - 1) * 0.018, cluster.y + (Math.floor(idx / 3) % 3 - 1) * 0.014],
      [cluster.x + (idx % 5 - 2) * 0.009, cluster.y + 0.018],
    ]
    return {
      id: d.id,
      weather: d.weather_id,
      agency: d.agency_rating,
      residue: d.residue_tags,
      path,
    }
  })

  // Incorporate resonances as echoes (they color the next weave)
  const echoMap = new Map<string, string[]>()
  for (const r of resonances) {
    if (!echoMap.has(r.cluster_key)) echoMap.set(r.cluster_key, [])
    if (echoMap.get(r.cluster_key)!.length < 2) {
      echoMap.get(r.cluster_key)!.push(r.resonance_text)
    }
  }
  const echoes: Echo[] = []
  echoMap.forEach((texts, key) => {
    texts.forEach(t => echoes.push({ cluster_key: key, text: t }))
  })

  // Poetic, civic, non-prescriptive summary lines — the heart of the replay
  const dominant = sortedBuckets[0]?.[0]
  const held = active.filter(d => d.unresolved_text && d.unresolved_text.trim().length > 4).length
  const lowAgency = active.filter(d => d.agency_rating < 38).length

  const lines: string[] = []

  if (dominant) {
    lines.push(`In this pass the most common remainder is ${humanLabel(dominant).toLowerCase()}.`)
  }
  if (meanAgency < 42) {
    lines.push(`Mean felt agency sits at ${meanAgency}. Most who placed something here experienced the edit as largely required rather than chosen.`)
  } else if (meanAgency > 68) {
    lines.push(`Mean felt agency is ${meanAgency}. The pattern still carries a surprising amount of perceived discretion.`)
  } else {
    lines.push(`Agency across the weave averages ${meanAgency} — the middle of the dial, where structure and choice are hardest to separate.`)
  }
  if (lowAgency > active.length * 0.55) {
    lines.push(`More than half the remainders in this window were deposited under conditions the narrator experienced as narrow.`)
  }
  if (held > 2) {
    lines.push(`The commons is currently carrying ${held} explicitly unresolved questions that have not been asked of anyone yet.`)
  }
  if (echoes.length > 0) {
    lines.push(`Recent witnesses have added ${echoes.length} resonances that are now part of the pattern.`)
  }
  if (lines.length < 3) {
    lines.push('The weave is still thin. More deposits will make the knots and the gaps clearer.')
  }

  // Keep it to 3–5 lines max, civic tone
  const summary_lines = lines.slice(0, 5)

  return {
    summary_lines,
    stats: {
      depositCount: active.length,
      meanAgency,
      dominantWeather: mostCommonWeather(active),
      knotCount: clusters.length,
      heldQuestions: held,
    },
    weave: { threads, clusters, echoes },
  }
}

function themeKey(d: Deposit): string {
  const text = (d.optimization_text + ' ' + (d.unresolved_text || '')).toLowerCase()
  if (text.match(/reply|respond|email|message|ping|slack|notification/)) return 'responsiveness'
  if (text.match(/kid|child|partner|family|care|dinner|bedtime|parent/)) return 'care_negotiations'
  if (text.match(/team|colleague|boss|manager|promotion|deadline|work/)) return 'professional_surface'
  if (text.match(/boundary|limit|no|capacity|already|spread/)) return 'boundary_maintenance'
  if (text.match(/tired|exhaust|sleep|evening|weekend|rest/)) return 'rest_and_recognition'
  return 'other_optimizations'
}

function humanLabel(key: string): string {
  switch (key) {
    case 'responsiveness': return 'Protecting the appearance of immediate responsiveness'
    case 'care_negotiations': return 'Postponed negotiations of care and presence'
    case 'professional_surface': return 'Maintaining the professional surface'
    case 'boundary_maintenance': return 'The quiet work of holding a boundary alone'
    case 'rest_and_recognition': return 'Trading rest for the feeling of being on top of things'
    default: return 'Small daily edits that keep the optimized story intact'
  }
}

function mostCommonResidue(deposits: Deposit[]): ResidueTag {
  const counts = new Map<ResidueTag, number>()
  for (const d of deposits) for (const t of d.residue_tags) counts.set(t, (counts.get(t) || 0) + 1)
  let best: ResidueTag = 'hollowness'
  let max = 0
  counts.forEach((v, k) => { if (v > max) { max = v; best = k } })
  return best
}

function mostCommonWeather(deposits: Deposit[]): string {
  const counts = new Map<WeatherId, number>()
  for (const d of deposits) counts.set(d.weather_id, (counts.get(d.weather_id) || 0) + 1)
  let best: WeatherId = 'low_static'
  let max = 0
  counts.forEach((v, k) => { if (v > max) { max = v; best = k } })
  return WEATHERS.find(w => w.id === best)?.label || 'Low Static'
}

// Generate a high-entropy receipt shown only once to the depositor
export function makeReceipt(): string {
  const a = new Uint8Array(18)
  crypto.getRandomValues(a)
  return Array.from(a, b => b.toString(16).padStart(2, '0')).join('').slice(0, 22)
}

// Simple session secret generator (client only)
export function makeSecret(): string {
  const a = new Uint8Array(16)
  crypto.getRandomValues(a)
  return 'r_' + Array.from(a, b => b.toString(16).padStart(2, '0')).join('')
}

// Very light client-side rate hint (real limits are enforced server-side)
export function canDeposit(lastDepositTs: number | null): boolean {
  if (!lastDepositTs) return true
  return (Date.now() - lastDepositTs) > 1000 * 60 * 18 // ~18 min between meaningful deposits
}
